import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { EmptyState, Loader } from '@lms/ui';
import { ListRange } from '@/components/ListPager';
import { SearchInput } from '@/components/SearchInput';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { useDebouncedSearch } from '@/hooks/useDebouncedValue';
import { listResource } from '@/lib/api';
import { gradebookCourseHref } from '@/lib/course-admin-navigation';

interface CourseRow {
  id: string;
  name: string;
  code?: string;
  shortDescription?: string;
}

const PER_PAGE = 12;

export function CalificacionesPage() {
  const [searchParams] = useSearchParams();
  const { value: search, setValue: setSearch, debounced, page, setPage } = useDebouncedSearch(
    searchParams.get('search') ?? '',
  );
  const appliedSearch = debounced.trim();

  const { data, isLoading, error } = useQuery({
    queryKey: ['courses', 'gradebook', appliedSearch, page],
    queryFn: () =>
      listResource<CourseRow>('/courses', {
        page,
        perPage: PER_PAGE,
        ...(appliedSearch ? { search: appliedSearch } : {}),
      }),
  });

  const courses = data?.data ?? [];
  const total = data?.meta?.total ?? courses.length;
  const firstItem = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const lastItem = Math.min(page * PER_PAGE, total);
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const paged = courses;
  const hasSearch = search.trim().length > 0;

  return (
    <div className="lms-gradebook-index lms-site-admin-hub-page font-sans">
      <header className="lms-gradebook-index__header mb-6">
        <div className="flex items-start gap-4">
          <HubBackLink to="/administracion?tab=grades" label="Volver a Administración" />
          <div className="min-w-0">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Libro de Calificaciones</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              Gestión de calificaciones por curso
            </p>
          </div>
        </div>

        <form method="get" action="/calificaciones" className="lms-gradebook-index__toolbar" onSubmit={(event) => event.preventDefault()}>
          <SearchInput
            name="search"
            value={search}
            onChange={setSearch}
            placeholder="Buscar curso"
            aria-label="Buscar curso"
            className="lms-gradebook-index__search"
          />
          <div className="lms-gradebook-index__toolbar-actions">
            {hasSearch ? (
              <Link
                to="/calificaciones"
                className="lms-gradebook-index__clear-btn"
                onClick={() => {
                  setSearch('');
                }}
              >
                Limpiar
              </Link>
            ) : (
              <span className="lms-gradebook-index__clear-btn lms-gradebook-index__clear-btn--muted">Limpiar</span>
            )}
          </div>
        </form>
      </header>

      {error && (
        <p className="text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <>
          <div className="lms-gradebook-index__grid">
            {paged.length === 0 ? (
              <div className="lms-gradebook-index__empty">
                <EmptyState title="Sin cursos" description="No hay cursos que coincidan con la búsqueda." />
              </div>
            ) : (
              paged.map((course) => {
                const showUrl = gradebookCourseHref(course.id, appliedSearch);
                return (
                  <article key={course.id} className="lms-gradebook-index__card">
                    <div className="lms-gradebook-index__card-body">
                      <div className="lms-gradebook-index__card-top">
                        {course.code && <span className="lms-gradebook-index__code">{course.code}</span>}
                      </div>
                      <h3 className="lms-gradebook-index__card-title">
                        <Link to={showUrl}>{course.name}</Link>
                      </h3>
                      {course.shortDescription ? (
                        <p className="lms-gradebook-index__card-desc">{course.shortDescription}</p>
                      ) : (
                        <p className="lms-gradebook-index__card-desc lms-gradebook-index__card-desc--empty">&nbsp;</p>
                      )}
                      <div className="lms-gradebook-index__card-footer">
                        <Link to={showUrl} className="lms-gradebook-index__cta">
                          <span className="material-symbols-outlined" aria-hidden="true">
                            analytics
                          </span>
                          Ver gradebook
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })
            )}
          </div>

          {total > 0 && (
            <div className="lms-gradebook-index__pagination">
              <p className="lms-gradebook-index__pagination-meta">
                <ListRange start={firstItem} end={lastItem} total={total} />
              </p>
              {totalPages > 1 && (
                <div className="lms-gradebook-index__pagination-links">
                  <nav aria-label="Paginación">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={p === page ? 'font-semibold underline' : ''}
                        onClick={() => setPage(p)}
                        aria-current={p === page ? 'page' : undefined}
                      >
                        {p}
                      </button>
                    ))}
                  </nav>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
